import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { getApiUrl } from "../../config/api";
import { Calendar, Clock, BookOpen, GraduationCap, Loader2, Plus, Edit2, Trash2, X, Check, Coffee, Layers, Filter, MoreVertical, LayoutGrid, CalendarDays, History, ShieldCheck, Timer, CheckCircle2, AlertCircle } from "lucide-react";
import "../../styles/DashboardLayout.css";
import "../../styles/UtopiaPages.css";

interface ScheduleEntry {
  id: string;
  dayOfWeek: string;
  startTime: string;
  endTime: string;
  classId: string;
  subjectId: string | null;
  isBreak?: boolean;
  subject: { name: string; };
}

interface Class { id: string; name: string; studentCount?: number; }
interface Subject { id: string; name: string; }
interface TimeSlot { startTime: string; endTime: string; sessionNumber: number; }

const Timetable: React.FC = () => {
  const token = localStorage.getItem("token");
  const role = (localStorage.getItem("role") || "").toLowerCase();
  const isAdmin = ["admin", "headteacher", "superadmin"].includes(role);

  const [loading, setLoading] = useState(true);
  const [loadingSchedule, setLoadingSchedule] = useState(false);
  const [classes, setClasses] = useState<Class[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [selectedClassId, setSelectedClassId] = useState<string>("");
  const [schedule, setSchedule] = useState<ScheduleEntry[]>([]);
  const [message, setMessage] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState<ScheduleEntry | null>(null);
  const [formData, setFormData] = useState({ dayOfWeek: "MONDAY", startTime: "07", endTime: "08", subjectId: "", isBreak: false });
  const [classSearch, setClassSearch] = useState("");

  const DAYS_OF_WEEK = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY"];
  const NUM_SESSIONS = 8;
  const HOURS = Array.from({ length: 15 }, (_, i) => (7 + i).toString().padStart(2, '0'));

  const timeSlots = useMemo(() => {
    const slots: TimeSlot[] = [];
    for (let i = 0; i < NUM_SESSIONS; i++) {
      const h = 7 + i;
      slots.push({
        startTime: `${h.toString().padStart(2, '0')}:00`,
        endTime: `${(h + 1).toString().padStart(2, '0')}:00`,
        sessionNumber: i + 1
      });
    }
    return slots;
  }, []);

  const fetchClasses = useCallback(async () => {
    if (!token) return;
    try {
      const endpoint = isAdmin ? "academics/classes" : "teacher/dashboard";
      const res = await axios.get(getApiUrl(endpoint), { headers: { Authorization: `Bearer ${token}` } });
      const cls = res.data.classes || (Array.isArray(res.data) ? res.data : []);
      setClasses(cls);

      // If no class is selected and we have classes, select the first one
      if (cls.length > 0 && !selectedClassId) {
        setSelectedClassId(cls[0].id);
      }
    } catch (err) {
      console.error("Error fetching classes:", err);
    } finally {
      setLoading(false);
    }
  }, [token, selectedClassId, isAdmin]);

  useEffect(() => { fetchClasses(); }, [fetchClasses]);

  const fetchSubjects = useCallback(async () => {
    if (!selectedClassId || !token) { setSubjects([]); return; }
    try {
      const res = await axios.get(getApiUrl(`academics/classes/${selectedClassId}/subjects`), { headers: { Authorization: `Bearer ${token}` } });
      setSubjects(Array.isArray(res.data) ? res.data : []);
    } catch { setSubjects([]); }
  }, [selectedClassId, token]);

  useEffect(() => { fetchSubjects(); }, [fetchSubjects]);

  const fetchSchedule = useCallback(async () => {
    if (!selectedClassId || !token) { setSchedule([]); return; }
    setLoadingSchedule(true);
    try {
      const res = await axios.get(getApiUrl(`schedule/class/${selectedClassId}`), { headers: { Authorization: `Bearer ${token}` } });
      setSchedule(Array.isArray(res.data) ? res.data : []);
    } catch { setSchedule([]); }
    finally { setLoadingSchedule(false); }
  }, [selectedClassId, token]);

  useEffect(() => { fetchSchedule(); }, [fetchSchedule]);

  const formatTime = (iso: string) => {
    try { return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
  };

  const getEntry = (day: string, slot: TimeSlot) => schedule.find(e => e.dayOfWeek === day && formatTime(e.startTime) === slot.startTime);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Validation
    if (!selectedClassId) {
      setMessage("Please select a class first.");
      setTimeout(() => setMessage(""), 3000);
      return;
    }

    if (!formData.isBreak && !formData.subjectId) {
      setMessage("Please select a subject or mark as break.");
      setTimeout(() => setMessage(""), 3000);
      return;
    }

    if (parseInt(formData.startTime) >= parseInt(formData.endTime)) {
      setMessage("End time must be after start time.");
      setTimeout(() => setMessage(""), 3000);
      return;
    }

    setIsSubmitting(true);
    setMessage("");

    try {
      // Create date objects with proper time handling
      const today = new Date();
      const isoStart = new Date(today.getFullYear(), today.getMonth(), today.getDate(), parseInt(formData.startTime), 0, 0);
      const isoEnd = new Date(today.getFullYear(), today.getMonth(), today.getDate(), parseInt(formData.endTime), 0, 0);

      const payload = {
        dayOfWeek: formData.dayOfWeek,
        startTime: isoStart.toISOString(),
        endTime: isoEnd.toISOString(),
        classId: selectedClassId,
        subjectId: formData.isBreak ? null : formData.subjectId,
        isBreak: formData.isBreak
      };

      if (editingEntry) {
        await axios.put(getApiUrl(`schedule/${editingEntry.id}`), payload, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axios.post(getApiUrl("schedule"), payload, { headers: { Authorization: `Bearer ${token}` } });
      }

      setMessage("Temporal registry updated.");
      fetchSchedule();
      setShowModal(false);
      setFormData({ dayOfWeek: "MONDAY", startTime: "08", endTime: "09", subjectId: "", isBreak: false });
      setTimeout(() => setMessage(""), 3000);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || "Failed to update timetable. Please try again.";
      setMessage(errorMessage);
      setTimeout(() => setMessage(""), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this timetable entry?")) return;
    try {
      await axios.delete(getApiUrl(`schedule/${id}`), { headers: { Authorization: `Bearer ${token}` } });
      fetchSchedule();
    } catch { }
  };

  return (
    <div className="dashboard-container">
      <main className="dashboard-main">
        <div className="page-title-section">
          <div>
            <h1>Academic Chronology</h1>
            <p className="page-subtitle">Manage class timetables and schedules for all classes</p>
          </div>
          {message && (
            <div className={`fixed top-20 right-8 z-50 p-4 rounded-2xl shadow-2xl flex items-center gap-3 animate-in fade-in slide-in-from-top-4 transition-all ${message.includes("Failed") || message.includes("Please") || message.includes("must")
              ? "bg-red-50 border border-red-200 text-red-700"
              : "bg-teal-50 border border-teal-200 text-teal-700"
              }`}>
              {message.includes("Failed") || message.includes("Please") || message.includes("must") ? (
                <AlertCircle size={18} />
              ) : (
                <CheckCircle2 size={18} />
              )}
              <span className="text-sm font-semibold">{message}</span>
            </div>
          )}
          <div className="flex flex-wrap gap-4 items-center">
            {isAdmin && (
              <div className="flex items-center gap-3 bg-white px-4 py-2.5 rounded-2xl border border-slate-100 shadow-sm transition-all focus-within:border-teal-400 focus-within:ring-4 focus-within:ring-teal-50">
                <Filter size={14} className="text-slate-400" />
                <input
                  type="text"
                  placeholder="Search class..."
                  className="bg-transparent border-none outline-none text-[10px] font-black uppercase tracking-widest text-slate-800 w-32"
                  value={classSearch}
                  onChange={e => setClassSearch(e.target.value)}
                />
              </div>
            )}

            <div className="flex items-center gap-3 bg-white px-6 py-3 rounded-2xl border border-slate-100 shadow-sm transition-all hover:border-teal-200">
              <Layers size={16} className="text-teal-500" />
              <select
                className="bg-transparent border-none outline-none text-xs font-black uppercase tracking-widest text-slate-700 cursor-pointer max-w-[200px]"
                value={selectedClassId}
                onChange={e => setSelectedClassId(e.target.value)}
              >
                <option value="">Select Class...</option>
                {classes
                  .filter(c => c.name.toLowerCase().includes(classSearch.toLowerCase()))
                  .map(c => <option key={c.id} value={c.id}>{c.name}</option>)
                }
              </select>
            </div>

            {(isAdmin || role === "teacher") && (
              <button
                className="utopia-btn-primary flex items-center gap-2"
                onClick={() => {
                  setEditingEntry(null);
                  setFormData({ dayOfWeek: "MONDAY", startTime: "08", endTime: "09", subjectId: "", isBreak: false });
                  setShowModal(true);
                }}
              >
                <Plus size={18} /> Append Session
              </button>
            )}
          </div>
        </div>

        {loadingSchedule ? (
          <div className="p-40 text-center text-slate-200"><LayoutGrid className="animate-pulse mx-auto opacity-10" size={80} /></div>
        ) : (
          <div className="utopia-page-container overflow-hidden">
            <div className="overflow-x-auto">
              <table className="utopia-table border-spacing-0">
                <thead>
                  <tr>
                    <th className="w-24 text-center">Session</th>
                    <th className="w-32 text-center">Interval</th>
                    {DAYS_OF_WEEK.map(d => <th key={d} className="text-center w-48 font-black uppercase tracking-widest text-[10px] text-slate-700">{d}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {timeSlots.map(slot => (
                    <tr key={slot.sessionNumber} className="hover:bg-slate-50/30 transition-colors">
                      <td className="text-center border-r border-slate-50/50"><span className="text-[10px] font-black text-slate-400 font-mono">#{(slot.sessionNumber).toString().padStart(2, '0')}</span></td>
                      <td className="text-center border-r border-slate-50/50">
                        <div className="flex flex-col gap-0.5">
                          <span className="text-[11px] font-black text-slate-700 font-mono tracking-tight">{slot.startTime}</span>
                          <span className="text-[11px] font-black text-slate-500 font-mono tracking-tight">{slot.endTime}</span>
                        </div>
                      </td>
                      {DAYS_OF_WEEK.map(day => {
                        const entry = getEntry(day, slot);
                        return (
                          <td key={`${day}-${slot.sessionNumber}`} className="p-1.5 h-32">
                            {entry ? (
                              <div className={`h-full w-full rounded-[2.5rem] p-5 flex flex-col justify-between group transition-all duration-500 border relative overflow-hidden ${entry.isBreak ? 'bg-amber-50 border-amber-100/50 hover:bg-amber-100/50' : 'bg-white border-slate-100 hover:border-teal-200 hover:shadow-xl hover:shadow-teal-50'
                                }`}>
                                <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-transparent to-black/[0.02] rounded-full translate-x-1/2 -translate-y-1/2"></div>
                                <div className="flex items-center justify-between relative z-10">
                                  {entry.isBreak ? <Coffee size={14} className="text-amber-500" /> : <BookOpen size={14} className="text-teal-500" />}
                                  {(isAdmin || role === "teacher") && (
                                    <div className="flex gap-2 translate-y-[-4px] opacity-0 group-hover:opacity-100 transition-all duration-300 scale-90 group-hover:scale-100">
                                      <button onClick={() => {
                                        const startHour = new Date(entry.startTime).getHours().toString().padStart(2, '0');
                                        const endHour = new Date(entry.endTime).getHours().toString().padStart(2, '0');
                                        setEditingEntry(entry);
                                        setFormData({
                                          dayOfWeek: entry.dayOfWeek,
                                          startTime: startHour,
                                          endTime: endHour,
                                          subjectId: entry.subjectId || "",
                                          isBreak: entry.isBreak || false
                                        });
                                        setShowModal(true);
                                      }} className="p-1.5 rounded-full bg-white shadow-sm text-blue-500 hover:bg-blue-50 transition-colors"><Edit2 size={12} /></button>
                                      <button onClick={() => handleDelete(entry.id)} className="p-1.5 rounded-full bg-white shadow-sm text-rose-500 hover:bg-rose-50 transition-colors"><Trash2 size={12} /></button>
                                    </div>
                                  )}
                                </div>
                                <div className="relative z-10">
                                  <span className={`text-[10px] font-black uppercase tracking-widest block mb-1 ${entry.isBreak ? 'text-amber-400' : 'text-slate-300'}`}>
                                    {entry.isBreak ? "Break" : "Period"}
                                  </span>
                                  <h4 className={`text-xs font-black uppercase tracking-tight line-clamp-2 ${entry.isBreak ? 'text-amber-800' : 'text-slate-800'}`}>
                                    {entry.subject?.name || "RECREATION SLOT"}
                                  </h4>
                                </div>
                              </div>
                            ) : (
                              (isAdmin || role === "teacher") ? (
                                <button onClick={() => {
                                  const startHour = slot.startTime.split(':')[0].padStart(2, '0');
                                  const endHour = slot.endTime.split(':')[0].padStart(2, '0');
                                  setFormData({ dayOfWeek: day, startTime: startHour, endTime: endHour, subjectId: "", isBreak: false });
                                  setEditingEntry(null);
                                  setShowModal(true);
                                }} className="h-full w-full rounded-[2.5rem] border-2 border-dashed border-slate-50 hover:border-teal-100 hover:bg-teal-50/30 transition-all flex flex-col items-center justify-center gap-2 group">
                                  <div className="w-8 h-8 rounded-full bg-slate-50 text-slate-300 flex items-center justify-center group-hover:bg-white group-hover:text-teal-500 group-hover:shadow-sm transition-all"><Plus size={16} /></div>
                                  <span className="text-[10px] font-black text-slate-200 uppercase tracking-widest group-hover:text-teal-600 transition-all">Allocate</span>
                                </button>
                              ) : (
                                <div className="h-full w-full rounded-[2.5rem] border border-slate-50 flex flex-col items-center justify-center opacity-30">
                                  <Clock size={16} className="text-slate-200" />
                                </div>
                              )
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      {showModal && (
        <div className="utopia-modal-overlay">
          <div className="utopia-modal-content max-w-4xl p-0 flex flex-col max-h-[95vh] overflow-hidden">
            <div className="utopia-modal-header bg-slate-50 p-8 border-b border-slate-100 shrink-0">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white rounded-2xl shadow-sm flex items-center justify-center text-teal-600 shrink-0"><Timer size={24} /></div>
                <div>
                  <h3 className="text-xl font-black text-slate-800 tracking-tight">{editingEntry ? "Edit Timetable Entry" : "Add Timetable Entry"}</h3>
                  <p className="text-xs text-slate-400 font-medium">Assign subjects to specific time slots</p>
                </div>
              </div>
              <button onClick={() => setShowModal(false)} className="p-3 hover:bg-white rounded-full transition-all shadow-sm text-slate-400 shrink-0"><X size={20} /></button>
            </div>

            <div className="flex-1 overflow-hidden">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-0 h-full">
                <div className="p-8 border-r border-slate-50 flex flex-col">
                  <form onSubmit={handleSubmit} className="space-y-5 flex-1 flex flex-col">
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Target Day</label>
                      <select className="utopia-input h-14 text-sm font-black uppercase tracking-tight" value={formData.dayOfWeek} onChange={e => setFormData({ ...formData, dayOfWeek: e.target.value })}>
                        {DAYS_OF_WEEK.map(d => <option key={d} value={d}>{d}</option>)}
                      </select>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Start Hour</label>
                        <select className="utopia-input h-14 text-sm font-black" value={formData.startTime} onChange={e => setFormData({ ...formData, startTime: e.target.value })}>
                          {HOURS.map(h => <option key={h} value={h}>{h}:00</option>)}
                        </select>
                      </div>
                      <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">End Hour</label>
                        <select className="utopia-input h-14 text-sm font-black" value={formData.endTime} onChange={e => setFormData({ ...formData, endTime: e.target.value })}>
                          {HOURS.map(h => <option key={h} value={h}>{h}:00</option>)}
                        </select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Curriculum Paradigim</label>
                      <select className="utopia-input h-14 text-sm font-black tracking-tight" value={formData.subjectId} onChange={e => setFormData({ ...formData, subjectId: e.target.value })} disabled={formData.isBreak}>
                        <option value="">Select Subject...</option>
                        {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                      </select>
                    </div>

                    <div className="flex items-center gap-4 p-4 bg-slate-50/50 rounded-3xl border border-slate-100 transition-all hover:border-amber-200">
                      <input type="checkbox" className="w-6 h-6 accent-amber-500 cursor-pointer shrink-0" checked={formData.isBreak} onChange={e => setFormData({ ...formData, isBreak: e.target.checked })} />
                      <div>
                        <p className="text-xs font-black text-slate-700 uppercase tracking-tight">Recreation Override</p>
                        <p className="text-[10px] text-slate-400 font-medium">Designate as break interval (suspends curriculum)</p>
                      </div>
                    </div>

                    <div className="mt-auto pt-4">
                      <button type="submit" className="utopia-btn-primary w-full py-3 shadow-2xl shadow-teal-100 flex items-center justify-center gap-3" disabled={isSubmitting}>
                        {isSubmitting ? <Loader2 className="animate-spin" size={18} /> : <ShieldCheck size={18} />}
                        Finalize Allocation
                      </button>
                    </div>
                  </form>
                </div>

                <div className="bg-slate-50/50 p-10 flex flex-col justify-center border-l border-white relative">
                  <CalendarDays size={180} className="absolute -bottom-10 -right-10 text-white opacity-40 -rotate-12 pointer-events-none" />
                  <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-8 relative z-10">Timetable Information</h4>

                  <div className="space-y-8 relative z-10">
                    <div className="flex items-center gap-5">
                      <div className="w-16 h-16 bg-white rounded-3xl shadow-sm flex items-center justify-center text-teal-600 border border-slate-100 shrink-0"><Timer size={28} /></div>
                      <div>
                        <p className="text-[9px] font-black text-slate-300 uppercase tracking-widest mb-1">Target Interval</p>
                        <p className="text-2xl font-black text-slate-800 tracking-tighter">{formData.startTime}:00 - {formData.endTime}:00</p>
                      </div>
                    </div>

                    <div className="p-6 bg-white/80 backdrop-blur-sm rounded-3xl border border-white shadow-sm">
                      <p className="text-[10px] font-medium text-slate-500 leading-relaxed italic">
                        "All timetable entries are synchronized across student and teacher schedules in real-time. Ensure no overlaps exist within the class timetable."
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 bg-teal-500 rounded-2xl text-white shadow-lg shadow-teal-100">
                        <p className="text-[8px] font-black uppercase tracking-widest opacity-60">Status</p>
                        <p className="text-xs font-black uppercase">{editingEntry ? "Updating" : "New Entry"}</p>
                      </div>
                      <div className="p-4 bg-white rounded-2xl border border-slate-100 shadow-sm">
                        <p className="text-[8px] font-black uppercase tracking-widest text-slate-300">Class</p>
                        <p className="text-xs font-black text-slate-800 uppercase tracking-tighter truncate">{classes.find(c => c.id === selectedClassId)?.name || "N/A"}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Timetable;











